import { gql, useMutation } from '@apollo/client'
import { Modal, notification } from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import epsClient from 'enrollment-platform/api/eps.client'

const SYNC_CENSUS_MUTATION = gql`
  mutation SyncCensus($input: SyncCensusInput!) {
    syncCensus(input: $input) {
      syncTime
    }
  }
`

interface HrisConfiguration {
  isCensusSyncEnabled?: boolean
  webHookUrl?: string | null
  isCMv2APIEnabled?: boolean
}

interface SyncCensusMutationData {
  syncCensus?: {
    syncTime?: string | null
  } | null
}

interface SyncCensusMutationVariables {
  input: {
    clientPublicKey: string
  }
}

const getHrisConfiguration = async (clientPublicKey: string) => {
  const { data } = await epsClient.get<HrisConfiguration>(
    `/customers/${clientPublicKey}/configuration/hris`
  )
  return data
}

export function useHrisSyncCensus({
  clientPublicKey,
  clientName,
  isPlatformAdmin,
}: {
  clientPublicKey?: string
  clientName?: string
  isPlatformAdmin: boolean
}) {
  const { data: hrisConfiguration } = useQuery({
    queryKey: ['hrisPayroll', clientPublicKey],
    queryFn: () => getHrisConfiguration(clientPublicKey as string),
    enabled: isPlatformAdmin && Boolean(clientPublicKey),
  })
  const [syncCensus, { loading: isSyncingCensus }] = useMutation<
    SyncCensusMutationData,
    SyncCensusMutationVariables
  >(SYNC_CENSUS_MUTATION)

  const showSyncCensusButton =
    isPlatformAdmin &&
    Boolean(clientPublicKey) &&
    hrisConfiguration?.isCensusSyncEnabled === true &&
    Boolean(hrisConfiguration?.webHookUrl?.trim()) &&
    hrisConfiguration?.isCMv2APIEnabled === true

  const handleSyncCensus = useCallback(async () => {
    if (!clientPublicKey) return

    try {
      const result = await syncCensus({
        variables: {
          input: { clientPublicKey },
        },
      })
      const syncTime = result.data?.syncCensus?.syncTime

      if (!syncTime) {
        notification.error({
          message: 'HRIS Sync failed',
          description:
            'No sync trigger time was returned. Please check the webhook and HRIS configuration before trying again.',
        })
        return
      }

      notification.success({
        message: 'HRIS Sync triggered',
        description: `Sync triggered at ${syncTime}. The census file will arrive asynchronously and can take 30 minutes to a few hours. Please do not re-trigger within about 24 hours.`,
      })
    } catch (err) {
      notification.error({
        message: 'HRIS Sync failed',
        description:
          (err as Error).message ??
          'Please check the webhook and HRIS configuration before trying again.',
      })
    }
  }, [clientPublicKey, syncCensus])

  const showSyncPromptModal = useCallback(() => {
    const clientLabel = clientName?.trim() || clientPublicKey

    Modal.confirm({
      title: `Start HRIS Sync for ${clientLabel}?`,
      content: `This triggers an asynchronous Flexspring census pull for ${clientLabel}. It can take 30 minutes to a few hours, so please do not re-trigger within about 24 hours.`,
      okText: 'Trigger Sync',
      cancelText: 'Cancel',
      onOk: handleSyncCensus,
    })
  }, [clientName, clientPublicKey, handleSyncCensus])

  return {
    showSyncCensusButton,
    isSyncingCensus,
    showSyncPromptModal,
  }
}
